/** Local BM25 search works immediately; optional embeddings refine its ranking. */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { allArticles } from '../Data/articles';
import { ProjectsData } from '../Data/projects';
import { ExperienceData } from '../Data/experience';
import { LAB_CATALOG } from '../Labs/labCatalog';
import { Bm25, toDocs, type CorpusDoc } from '../../lib/bm25';
import { fuse } from '../../lib/rrf';

const MONO = "'JetBrains Mono', 'Fira Code', 'Courier New', monospace";
const CACHE_KEY = 'sem_header_embeddings_v1';
const CACHE_HASH_KEY = 'sem_header_hash_v1';
type SearchState = 'idle' | 'loading' | 'ready' | 'error';
type Result = Pick<CorpusDoc, 'href' | 'title' | 'kind'>;

function cosineSim(a: number[], b: number[]): number {
    let dot = 0, na = 0, nb = 0;
    for (let i = 0; i < a.length; i++) {
        dot += a[i] * b[i];
        na += a[i] * a[i];
        nb += b[i] * b[i];
    }
    return dot / (Math.sqrt(na) * Math.sqrt(nb) + 1e-10);
}

// Derived from the single LAB_CATALOG source of truth so search never drifts from Labs.
const LAB_DOCS = LAB_CATALOG.map(lab => ({
    id: lab.id,
    title: lab.title,
    category: lab.category,
    summary: `${lab.searchSummary} ${lab.desc}`,
}));

const HeaderSearch: React.FC = () => {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [state, setState] = useState<SearchState>('idle');
    const [results, setResults] = useState<Result[]>([]);
    const [activeIdx, setActiveIdx] = useState(-1);
    const extractorRef = useRef<any>(null);
    const embedsRef = useRef<number[][]>([]);
    const lexicalRef = useRef<Bm25 | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const resultRefs = useRef<(HTMLAnchorElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const requestRef = useRef(0);
    const loadingRef = useRef(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const close = useCallback((restoreFocus = false) => {
        requestRef.current++;
        setOpen(false);
        setQuery('');
        setResults([]);
        setActiveIdx(-1);
        if (restoreFocus) triggerRef.current?.focus();
    }, []);

    useEffect(() => { close(); }, [location.pathname, close]);

    // Global shortcut. Ignored inside any text field, so a slash typed into the
    // terminal panel or the chat composer still reaches its own handler.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.defaultPrevented || event.altKey || event.shiftKey) return;
            const target = event.target;
            if (target instanceof Element && target.closest('input, textarea, select, [contenteditable]')) return;
            const isSlash = event.key === '/' && !event.metaKey && !event.ctrlKey;
            const isCommandK = event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey);
            if (!isSlash && !isCommandK) return;
            event.preventDefault();
            setOpen(true);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        if (!open) return;
        inputRef.current?.focus();
        const onPointerDown = (event: PointerEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) close();
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [open, close]);

    const loadAndEmbed = useCallback(async () => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        setState('loading');
        try {
            // Vendored, not CDN-loaded: a dynamic import() has no `integrity` attribute to
            // pin against (unlike a <script> tag), so a CDN-hosted entry point can change
            // under us with nothing to catch it. Same-origin removes that trust entirely.
            const { pipeline } = await import(
                /* webpackIgnore: true */
                '/vendor/transformers@3.3.3.min.js' as any
            );
            const extractor = await pipeline('feature-extraction', 'Xenova/all-MiniLM-L6-v2');
            const hash = JSON.stringify(allArticles.map(a => [a.slug, a.title, a.summary, a.category]));
            let embeds: number[][] | null = null;
            // Storage denial or a damaged cache must never disable local search.
            try {
                if (localStorage.getItem(CACHE_HASH_KEY) === hash) {
                    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
                    if (Array.isArray(cached) && cached.length === allArticles.length &&
                        cached.every(row => Array.isArray(row) && row.length === 384 && row.every(Number.isFinite))) {
                        embeds = cached;
                    }
                }
            } catch { /* Recompute embeddings when storage is unavailable. */ }
            if (!embeds) {
                embeds = [];
                for (const article of allArticles) {
                    const out = await extractor(`${article.title} ${article.summary} ${article.category}`, { pooling: 'mean', normalize: true });
                    embeds.push(Array.from(out.data as Float32Array));
                }
                try {
                    localStorage.setItem(CACHE_KEY, JSON.stringify(embeds));
                    localStorage.setItem(CACHE_HASH_KEY, hash);
                } catch { /* Search also works without persistent caching. */ }
            }
            if (!mountedRef.current) return;
            extractorRef.current = extractor;
            embedsRef.current = embeds;
            setState('ready');
        } catch {
            if (mountedRef.current) setState('error');
        }
    }, []);

    const doSearch = useCallback(async (q: string, semantic = false) => {
        if (semantic && document.activeElement !== inputRef.current) return;
        const request = ++requestRef.current;
        setActiveIdx(-1);
        if (!q.trim()) { setResults([]); return; }
        if (!lexicalRef.current) {
            lexicalRef.current = new Bm25(toDocs({
                articles: allArticles.map(a => ({ id: a.slug, title: a.title, category: a.category, summary: a.summary })),
                labs: LAB_DOCS,
                projects: ProjectsData.map(p => ({ ...p, summary: p.overview })),
                experience: ExperienceData.map(r => ({ ...r, points: r.experience })),
            }));
        }
        const lex = lexicalRef.current.search(q, 8);
        const byId = new Map<string, Result>(lex.map(m => [m.doc.id, m.doc]));
        // Publish before any network or inference work.
        setResults(lex.slice(0, 6).map(m => m.doc));
        if (!semantic || !extractorRef.current || !embedsRef.current.length) return;
        try {
            const out = await extractorRef.current(q, { pooling: 'mean', normalize: true });
            if (request !== requestRef.current || !mountedRef.current) return;
            const qVec: number[] = Array.from(out.data as Float32Array);
            const scored = allArticles
                .map((a, i) => ({ a, score: cosineSim(qVec, embedsRef.current[i]) }))
                .sort((x, y) => y.score - x.score)
                .filter(r => r.score > 0.15).slice(0, 8);
            for (const { a } of scored) {
                byId.set(`article:${a.slug}`, { href: `#/article/${a.slug}`, title: a.title, kind: 'article' });
            }
            const ranked = fuse([
                { key: 'lexical', weight: 1, order: lex.map(m => m.doc.id) },
                { key: 'semantic', weight: 1.2, order: scored.map(r => `article:${r.a.slug}`) },
            ], Array.from(byId.keys())).slice(0, 6);
            // Do not reorder links while somebody is navigating them.
            if (document.activeElement === inputRef.current) setResults(ranked.map(r => byId.get(r.id)!));
        } catch {
            if (request === requestRef.current && mountedRef.current) setState('error');
        }
    }, []);

    const handleInput = (value: string) => {
        setQuery(value);
        void doSearch(value);
        if (value.trim() && state === 'idle') void loadAndEmbed();
    };

    useEffect(() => {
        if (!open || state !== 'ready' || !query.trim()) return;
        const timer = setTimeout(() => { void doSearch(query, true); }, 300);
        return () => clearTimeout(timer);
    }, [open, state, query, doSearch]);

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.nativeEvent.isComposing || event.keyCode === 229) return;
        if (event.key === 'Escape') { event.preventDefault(); close(true); return; }
        if (!results.length) return;
        const index = resultRefs.current.findIndex(link => link === document.activeElement);
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            resultRefs.current[Math.min(index + 1, results.length - 1)]?.focus();
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (index <= 0) inputRef.current?.focus();
            else resultRefs.current[index - 1]?.focus();
        } else if (event.key === 'Enter' && event.target === inputRef.current) {
            event.preventDefault();
            resultRefs.current[0]?.click();
        }
    };

    return (
        <div ref={containerRef} className="header-search" style={{ position: 'relative' }} onKeyDown={handleKeyDown}
            onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) close(); }}>
            <button ref={triggerRef} className="header-search__trigger tap-hit"
                onClick={() => open ? close(true) : setOpen(true)}
                title="Search site — press / or ⌘K" aria-label="Search site. Press slash or Command K."
                aria-expanded={open} aria-controls={open ? 'site-search-panel' : undefined}
                style={{ background: open ? 'var(--bs-accent-fill)' : 'var(--bs-sunken)', border: `1px solid ${open ? 'var(--bs-accent-fill)' : 'var(--bs-rule)'}`, padding: '5px 10px', cursor: 'pointer', color: open ? 'var(--bs-accent-ink)' : 'var(--bs-muted)', fontFamily: MONO, display: 'flex', alignItems: 'center', gap: 5, borderRadius: 9, transition: 'color 150ms, background 150ms, border-color 150ms' }}
            >
                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" />
                </svg>
                <span className="header-search__label" style={{ fontSize: 11, letterSpacing: '0.05em' }}>search</span>
                <kbd className="header-search__label" aria-hidden="true" style={{ fontSize: 10, padding: '1px 5px', border: '1px solid var(--bs-rule)', borderRadius: 4, color: open ? 'var(--bs-accent-ink)' : 'var(--bs-dim)' }}>/</kbd>
            </button>
            {open && (
                <div id="site-search-panel" className="header-search__panel glass glass--quiet" style={{
                    // Fill, border and shadow come from the shared glass primitive.
                    position: 'absolute', top: 'calc(100% + 14px)', right: 0,
                    width: 380, maxWidth: 'calc(100vw - 24px)',
                    zIndex: 1200, fontFamily: MONO, maxHeight: 'calc(100dvh - 120px)', overflowY: 'auto',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 14 }}>
                        <input ref={inputRef} className="header-search__field" type="search"
                            value={query} onChange={e => handleInput(e.target.value)}
                            onFocus={() => setActiveIdx(-1)}
                            aria-label="Search articles, labs, projects and experience"
                            placeholder="Search the site…" autoComplete="off" spellCheck={false}
                            style={{ width: '100%', minWidth: 0, border: '1px solid var(--bs-dim)', background: 'var(--bs-ground)', color: 'var(--bs-ink)', padding: '10px 12px', fontFamily: MONO, fontSize: 13, caretColor: 'var(--bs-accent)' }}
                        />
                        <button onClick={() => close(true)} aria-label="Close search" style={{ background: 'none', border: 'none', color: 'var(--bs-muted)', padding: 8, cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
                            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                                <path d="m6 6 12 12M18 6 6 18" />
                            </svg>
                        </button>
                    </div>
                    <div role="status" style={{ padding: '0 14px 12px', color: 'var(--bs-muted)', fontSize: 11, lineHeight: 1.6 }}>
                        {query.trim() ? `${results.length} result${results.length === 1 ? '' : 's'}.` : 'Find articles, labs, projects and experience.'}
                        {state === 'loading' && ' Adding matches by meaning…'}
                        {state === 'error' && ' Keyword search available.'}
                    </div>
                    {results.length > 0 && (
                        <ul aria-label="Search results" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {results.map((result, i) => (
                                <li key={result.href + result.title}>
                                    <Link ref={el => { resultRefs.current[i] = el; }}
                                        to={result.href.replace(/^#/, '')}
                                        onClick={event => { if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) close(); }}
                                        onFocus={() => setActiveIdx(i)}
                                        className="header-search__result"
                                        style={{ display: 'block', padding: '12px 14px', color: i === activeIdx ? 'var(--bs-accent-ink)' : 'var(--bs-ink)', textDecoration: 'none', borderTop: '1px solid var(--bs-rule)', background: i === activeIdx ? 'var(--bs-accent-fill)' : 'transparent' }}
                                    >
                                        <span style={{ display: 'block', fontSize: 13, lineHeight: 1.5, overflowWrap: 'anywhere' }}>{result.title}</span>
                                        <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: i === activeIdx ? 'var(--bs-accent-ink)' : 'var(--bs-muted)' }}>{result.kind === 'role' ? 'experience' : result.kind}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                    {query.trim() && results.length === 0 && (
                        <p style={{ padding: '0 14px 14px', margin: 0, color: 'var(--bs-ink)', fontSize: 12 }}>Try a topic such as RAG, training or Docker.</p>
                    )}
                    <div style={{ padding: '10px 14px', borderTop: '1px solid var(--bs-rule)', color: 'var(--bs-muted)', fontSize: 10 }}>↑↓ navigate · enter open · esc close</div>
                </div>
            )}
        </div>
    );
};

export default HeaderSearch;
